import { useEffect, lazy, Suspense } from "react";

import { siteConfig, DEFAULT_ACCENT } from "../site-config";

// three.js is ~580 kB and drives a decorative layer — no reason to make the
// headline wait on it. Split out, it loads after the page has painted.
const Backdrop = lazy(() => import("./Backdrop").then((m) => ({ default: m.Backdrop })));
import { Nav } from "./Nav";
import { Hero } from "./Hero";
import { Marquee } from "./Marquee";
import { Services } from "./Services";
import { Work } from "./Work";
import { Approach } from "./Approach";
import { About } from "./About";
import { Workflows } from "./Workflows";
import { Contact } from "./Contact";
import { Footer } from "./Footer";

export function Site() {
  const { brand, operator, email, location, theme, accent, density, headlineKey } = siteConfig;

  // Theme + density are expressed as attributes on <html> so the CSS token
  // layer owns the switch.
  useEffect(() => {
    const html = document.documentElement;
    if (theme === "mist") html.setAttribute("data-theme", "mist");
    else html.removeAttribute("data-theme");
    html.setAttribute("data-density", density);
  }, [theme, density]);

  // A non-default accent overrides the whole --beam family.
  useEffect(() => {
    const root = document.documentElement;
    const beamProps = ["--beam", "--beam-halo", "--beam-soft", "--beam-hover", "--beam-press"];

    if (accent && accent !== DEFAULT_ACCENT) {
      root.style.setProperty("--beam", accent);
      root.style.setProperty("--beam-halo", `color-mix(in oklab, ${accent} 28%, transparent)`);
      root.style.setProperty("--beam-soft", `color-mix(in oklab, ${accent} 14%, transparent)`);
      root.style.setProperty("--beam-hover", `color-mix(in oklab, ${accent} 80%, white)`);
      root.style.setProperty("--beam-press", `color-mix(in oklab, ${accent} 80%, black)`);
    } else {
      beamProps.forEach((p) => root.style.removeProperty(p));
    }

    return () => beamProps.forEach((p) => root.style.removeProperty(p));
  }, [accent]);

  return (
    <>
      <Suspense fallback={null}>
        <Backdrop />
      </Suspense>
      <div
        data-site-scroll
        style={{
          height: "100vh",
          overflowY: "auto",
          position: "relative",
          zIndex: 1,
          background: "transparent",
          color: "var(--ink-2)",
        }}
      >
        <Nav brand={brand} />
        <Hero operator={operator} location={location} headlineKey={headlineKey} />
        <Marquee />
        <Services />
        <Work />
        <Approach />
        <About location={location} />
        <Workflows />
        <Contact email={email} />
        <Footer brand={brand} email={email} />
      </div>
    </>
  );
}
